import { useEffect, useRef } from 'react';
import { createPlugin } from '@backstage/core-plugin-api';
import {
  createTechDocsAddonExtension,
  TechDocsAddonLocations,
  useShadowRoot,
  useShadowRootElements,
} from '@backstage/plugin-techdocs-react';
import { TECHDOCS_SEARCH_TOOLBAR_CLASS } from './CustomTechDocsSearchToolbar';
import {
  TECHDOCS_DESKTOP_MEDIA_QUERY,
  TECHDOCS_DESKTOP_MIN_WIDTH,
} from './techDocsBreakpoints';

const STYLE_ID = 'techdocs-nav-scroll-fix';

/**
 * Wait for MkDocs nested-nav expand/collapse (~250ms) before scrolling the
 * active item into view. Slightly longer than the transition so layout is
 * settled.
 */
export const NAV_EXPAND_SETTLE_MS = 280;

/**
 * App layout scrolls inside ContentFrame (overflow:auto), not the window.
 * TechDocs only listens to window scroll, so the docs left nav stays locked to
 * the article's document position — you cannot scroll the nav to its first
 * items until the middle section is scrolled back to the top.
 *
 * Fix: treat the nearest overflow scroll parent as the scrollport, pin the
 * sidebars to it, and make each sidebar its own wheel scrollport.
 *
 * Styles are injected after MkDocs Material rules in the same shadow root, so
 * equal-specificity declarations win without !important except where Material
 * / TechDocs already use !important (padding on the sidebar).
 */
const SIDEBAR_SCROLL_CSS = `
@media screen and (min-width: ${TECHDOCS_DESKTOP_MIN_WIDTH}) {
  .md-sidebar {
    position: fixed;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    bottom: auto;
    padding-top: 0;
    opacity: 1;
  }
  .md-sidebar .md-sidebar__scrollwrap {
    height: auto;
    max-height: none;
    overflow: visible;
    margin: 0;
    padding-top: 0;
  }
  .md-sidebar .md-sidebar__inner {
    height: auto;
    padding-top: 0;
  }
  .md-sidebar--primary .md-nav__title {
    display: none;
  }
  .md-sidebar--primary .md-nav--primary,
  .md-sidebar--primary .md-nav__list {
    padding-top: 0;
    margin-top: 0;
  }
}
`;

/**
 * Nearest light-DOM ancestor that can scroll (e.g. ContentFrame).
 * Match on overflow style alone so the scroll listener is attached even when
 * the frame initially fits its content and only overflows later.
 */
export function findScrollParent(element: Element | null): HTMLElement | null {
  let current = element?.parentElement ?? null;
  while (current) {
    const { overflowY } = window.getComputedStyle(current);
    const canScrollY = overflowY === 'auto' || overflowY === 'scroll';
    if (canScrollY) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}

/**
 * If `position:fixed` is trapped by a transformed/filtered ancestor, a probe
 * with `top:0` reports that ancestor's top instead of 0.
 * Append inside the shadow root so the probe has a layout box (appending to
 * the host light DOM without a slot would always report 0).
 */
export function getFixedContainingBlockTop(shadowRoot: ShadowRoot): number {
  const probe = document.createElement('div');
  probe.style.cssText =
    'position:fixed;top:0;left:0;width:0;height:0;visibility:hidden;pointer-events:none;';
  shadowRoot.appendChild(probe);
  const top = probe.getBoundingClientRect().top;
  probe.remove();
  return top;
}

/** Scroll the active nav item within its sidebar only (not the page). */
export function scrollActiveNavIntoSidebar(shadowRoot: ShadowRoot): void {
  const activeItems = shadowRoot.querySelectorAll('li.md-nav__item--active');
  if (activeItems.length === 0) {
    return;
  }

  const lastActive = activeItems[activeItems.length - 1] as HTMLElement;
  const sidebar = lastActive.closest('.md-sidebar') as HTMLElement | null;
  if (!sidebar) {
    return;
  }

  const itemRect = lastActive.getBoundingClientRect();
  const wrapRect = sidebar.getBoundingClientRect();
  const isAbove = itemRect.top < wrapRect.top;
  const isBelow = itemRect.bottom > wrapRect.bottom;
  if (!isAbove && !isBelow) {
    return;
  }

  const delta = isAbove
    ? itemRect.top - wrapRect.top - 8
    : itemRect.bottom - wrapRect.bottom + 8;
  sidebar.scrollTop += delta;
}

function injectSidebarScrollStyles(shadowRoot: ShadowRoot): void {
  if (shadowRoot.getElementById(STYLE_ID)) {
    return;
  }
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = SIDEBAR_SCROLL_CSS;
  shadowRoot.appendChild(style);
}

export type SidebarAlignMetrics = {
  styleTop: number;
  height: number;
  viewportTop: number;
};

/**
 * Compute sidebar top/height so the nav pins to the search row (or the content
 * frame top once search has scrolled away), independent of article scroll.
 */
export function computeSidebarAlignMetrics(
  shadowRoot: ShadowRoot,
): SidebarAlignMetrics {
  const host = shadowRoot.host as HTMLElement;
  const scrollParent = findScrollParent(host);
  const frameRect = scrollParent?.getBoundingClientRect() ?? {
    top: 0,
    bottom: window.innerHeight,
    height: window.innerHeight,
  };

  const toolbar = document.querySelector(
    `.${TECHDOCS_SEARCH_TOOLBAR_CLASS}`,
  ) as HTMLElement | null;
  const toolbarRect = toolbar?.getBoundingClientRect();

  // Desired viewport Y for the top of the fixed sidebars.
  let viewportTop: number;
  if (toolbarRect && toolbarRect.bottom > frameRect.top + 1) {
    viewportTop = Math.max(toolbarRect.top, frameRect.top);
  } else {
    // Search scrolled away — sit flush with the content frame (not the article).
    viewportTop = frameRect.top;
  }
  viewportTop = Math.max(0, viewportTop);

  const containingBlockTop = getFixedContainingBlockTop(shadowRoot);
  const styleTop = viewportTop - containingBlockTop;

  const footer = shadowRoot.querySelector(
    '.md-container > .md-footer',
  ) as HTMLElement | null;
  const footerTop = footer?.getBoundingClientRect().top ?? frameRect.bottom;
  const viewportBottom = Math.min(footerTop, frameRect.bottom);
  const height = Math.max(0, viewportBottom - viewportTop);

  return { styleTop, height, viewportTop };
}

export function isTechDocsDesktopLayout(
  matchMedia: (query: string) => MediaQueryList = window.matchMedia,
): boolean {
  return matchMedia(TECHDOCS_DESKTOP_MEDIA_QUERY).matches;
}

function clearSidebarAlignStyles(sidebars: NodeListOf<HTMLElement>): void {
  sidebars.forEach(element => {
    element.style.removeProperty('top');
    element.style.removeProperty('height');
    element.style.removeProperty('opacity');
  });
}

/**
 * Pin docs sidebars to the content scrollport / search row so left-nav scroll
 * is independent of the middle article scroll. No-ops below the desktop
 * breakpoint so MkDocs' mobile drawer layout is left alone.
 */
export function alignSidebarsWithSearchToolbar(shadowRoot: ShadowRoot): void {
  const sidebars = shadowRoot.querySelectorAll(
    '.md-sidebar',
  ) as NodeListOf<HTMLElement>;

  if (!isTechDocsDesktopLayout()) {
    clearSidebarAlignStyles(sidebars);
    return;
  }

  const { styleTop, height } = computeSidebarAlignMetrics(shadowRoot);
  sidebars.forEach(element => {
    element.style.top = `${styleTop}px`;
    element.style.height = `${height}px`;
    element.style.setProperty('opacity', '1');
  });
}

/**
 * Convert WheelEvent.deltaY to CSS pixels. deltaMode may be lines or pages
 * (common for mouse wheels); adding the raw value after preventDefault would
 * only move a few pixels per notch.
 */
export function normalizeWheelDeltaY(
  event: WheelEvent,
  element: HTMLElement,
): number {
  switch (event.deltaMode) {
    case WheelEvent.DOM_DELTA_LINE: {
      const lineHeight =
        Number.parseFloat(window.getComputedStyle(element).lineHeight) || 16;
      return event.deltaY * lineHeight;
    }
    case WheelEvent.DOM_DELTA_PAGE:
      return event.deltaY * element.clientHeight;
    default:
      return event.deltaY;
  }
}

/**
 * When the pointer is over a docs sidebar, scroll that sidebar — never the
 * content frame / article.
 */
export function attachSidebarWheelLock(sidebar: HTMLElement): () => void {
  const onWheel = (event: WheelEvent) => {
    // Ctrl+wheel / trackpad pinch-to-zoom must keep native browser zoom.
    if (event.ctrlKey) {
      return;
    }

    const maxScroll = sidebar.scrollHeight - sidebar.clientHeight;
    if (maxScroll <= 0) {
      return;
    }

    const deltaY = normalizeWheelDeltaY(event, sidebar);
    const nextTop = sidebar.scrollTop + deltaY;
    const isScrollingUp = deltaY < 0;
    const isScrollingDown = deltaY > 0;
    const isAtTop = sidebar.scrollTop <= 0;
    const isAtBottom = sidebar.scrollTop >= maxScroll - 1;

    if ((isScrollingUp && !isAtTop) || (isScrollingDown && !isAtBottom)) {
      event.preventDefault();
      event.stopPropagation();
      sidebar.scrollTop = Math.min(maxScroll, Math.max(0, nextTop));
    } else if ((isScrollingUp && isAtTop) || (isScrollingDown && isAtBottom)) {
      // At a boundary: do not let the gesture scroll the article instead.
      event.preventDefault();
      event.stopPropagation();
    }
  };

  sidebar.addEventListener('wheel', onWheel, { passive: false });
  return () => {
    sidebar.removeEventListener('wheel', onWheel);
  };
}

/**
 * Content addon: left/right docs nav scroll independently of the article, even
 * when the app's ContentFrame (not window) is the scroll container.
 */
export const TechDocsNavScrollFixAddon = () => {
  const shadowRoot = useShadowRoot();
  const activeNavItems = useShadowRootElements(['li.md-nav__item--active']);
  const nestedToggles = useShadowRootElements([
    '.md-nav__item--nested .md-toggle',
  ]);
  const settleTimerRef = useRef<number | undefined>(undefined);
  const isAligningRef = useRef(false);

  useEffect(() => {
    if (!shadowRoot) {
      return undefined;
    }

    injectSidebarScrollStyles(shadowRoot);

    const host = shadowRoot.host as HTMLElement;
    const scrollParent = findScrollParent(host);
    const sidebars = Array.from(
      shadowRoot.querySelectorAll('.md-sidebar'),
    ) as HTMLElement[];
    const detachWheelLocks = sidebars.map(attachSidebarWheelLock);
    const desktopMedia = window.matchMedia(TECHDOCS_DESKTOP_MEDIA_QUERY);

    const align = () => {
      if (isAligningRef.current) {
        return;
      }
      isAligningRef.current = true;
      try {
        alignSidebarsWithSearchToolbar(shadowRoot);
      } finally {
        window.requestAnimationFrame(() => {
          isAligningRef.current = false;
        });
      }
    };

    const settleNavScroll = () => {
      align();
      scrollActiveNavIntoSidebar(shadowRoot);
    };

    align();
    settleTimerRef.current = window.setTimeout(
      settleNavScroll,
      NAV_EXPAND_SETTLE_MS,
    );

    const onToggle = () => {
      if (settleTimerRef.current !== undefined) {
        window.clearTimeout(settleTimerRef.current);
      }
      settleTimerRef.current = window.setTimeout(
        settleNavScroll,
        NAV_EXPAND_SETTLE_MS,
      );
    };

    for (const toggle of nestedToggles) {
      toggle.addEventListener('change', onToggle);
    }

    // Critical: ContentFrame scroll does not fire window "scroll".
    scrollParent?.addEventListener('scroll', align, { passive: true });
    window.addEventListener('resize', align);
    const onMediaChange = () => align();
    desktopMedia.addEventListener('change', onMediaChange);

    const styleObserver = new MutationObserver(() => {
      if (!isAligningRef.current) {
        align();
      }
    });
    for (const sidebar of sidebars) {
      styleObserver.observe(sidebar, {
        attributes: true,
        attributeFilter: ['style'],
      });
    }

    return () => {
      if (settleTimerRef.current !== undefined) {
        window.clearTimeout(settleTimerRef.current);
      }
      for (const toggle of nestedToggles) {
        toggle.removeEventListener('change', onToggle);
      }
      scrollParent?.removeEventListener('scroll', align);
      window.removeEventListener('resize', align);
      desktopMedia.removeEventListener('change', onMediaChange);
      styleObserver.disconnect();
      detachWheelLocks.forEach(detach => detach());
    };
  }, [shadowRoot, activeNavItems, nestedToggles]);

  return null;
};

const techDocsNavScrollFixPlugin = createPlugin({
  id: 'techdocs-nav-scroll-fix',
});

/** Registerable TechDocs addon for entity Docs tabs and the standalone reader. */
export const TechDocsNavScrollFix = techDocsNavScrollFixPlugin.provide(
  createTechDocsAddonExtension({
    name: 'TechDocsNavScrollFix',
    location: TechDocsAddonLocations.Content,
    component: TechDocsNavScrollFixAddon,
  }),
);
